/**
 * @file 动态 chunk 加载失败恢复
 * @description 处理发版后旧页面引用新旧 hash 不一致导致的动态 import 失败。
 */

/** 记录本页会话内已经自动恢复过的路由，避免重复刷新。 */
const recoveredChunkPaths = new Set<string>()

/** chunk 加载失败的浏览器与构建工具错误文案。 */
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk .* failed|error loading dynamically imported module/i

/** 浏览器错误事件、Promise 拒绝事件与 Vite preload 事件中可能携带错误文本的字段。 */
type ChunkErrorTextKey =
  | 'message'
  | 'reason'
  | 'error'
  | 'payload'
  | 'href'
  | 'src'
  | 'type'

/** 可递归提取 chunk 加载错误文案的对象结构。 */
interface ChunkErrorLike
  extends Partial<Record<ChunkErrorTextKey, ChunkErrorInput>> {}

/** chunk 恢复入口可能接收到的浏览器错误值。 */
type ChunkErrorInput =
  | string
  | number
  | boolean
  | Error
  | Event
  | ChunkErrorLike
  | null
  | undefined

/**
 * 从浏览器错误对象中提取可匹配的错误文案。
 * @param value 浏览器错误事件、Promise 拒绝原因或 Vite preload 事件。
 * @returns 可用于判断 chunk 加载失败的错误文本。
 */
const getChunkErrorText = (value: ChunkErrorInput): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message
  if (value instanceof Event && value.type === 'vite:preloadError') {
    return value.type
  }
  if (typeof value !== 'object') return String(value)

  const record = value as ChunkErrorLike
  return (
    [
      record.message,
      record.reason,
      record.error,
      record.payload,
      record.href,
      record.src,
      record.type
    ]
      .map(getChunkErrorText)
      .find(Boolean) || ''
  )
}

/**
 * 判断错误是否为动态 chunk 加载失败。
 * @param value 浏览器错误事件、Promise 拒绝原因或 Vite preload 事件。
 * @returns 是否命中动态 chunk 加载失败模式。
 */
export const isChunkLoadError = (value: ChunkErrorInput): boolean => {
  const message = getChunkErrorText(value)
  return (
    message === 'vite:preloadError' || CHUNK_LOAD_ERROR_PATTERN.test(message)
  )
}

/**
 * 将目标地址归一为当前站内路径。
 * @param targetHref 当前页面或目标页面完整地址。
 * @returns 含 path、query、hash 的站内刷新路径。
 */
const normalizeReloadPath = (targetHref: string): string => {
  const url = new URL(targetHref, window.location.href)
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * 判断指定路径是否已经执行过 chunk 自动恢复。
 * @param reloadKey 当前路径对应的恢复标记 key。
 * @returns 本页内存或 sessionStorage 是否已有恢复标记。
 */
const hasRecoveredPath = (reloadKey: string): boolean => {
  if (recoveredChunkPaths.has(reloadKey)) return true
  try {
    return sessionStorage.getItem(reloadKey) === '1'
  } catch {
    return false
  }
}

/**
 * 标记指定路径已经执行过 chunk 自动恢复。
 * @param reloadKey 当前路径对应的恢复标记 key。
 */
const markRecoveredPath = (reloadKey: string): void => {
  recoveredChunkPaths.add(reloadKey)
  try {
    sessionStorage.setItem(reloadKey, '1')
  } catch {
    // sessionStorage 不可用时仍保留内存标记
  }
}

/**
 * 对 chunk 加载失败执行单路径单次刷新恢复。
 * @param value 浏览器错误事件、Promise 拒绝原因或 Vite preload 事件。
 * @param targetHref 需要重新进入的目标地址，默认使用当前地址。
 * @returns 是否已经触发页面跳转恢复。
 */
export const recoverChunkLoadFailure = (
  value: ChunkErrorInput,
  targetHref = window.location.href
): boolean => {
  if (!isChunkLoadError(value)) return false

  const reloadPath = normalizeReloadPath(targetHref)
  const reloadKey = `chunk-reload:${reloadPath}`
  if (hasRecoveredPath(reloadKey)) return false

  markRecoveredPath(reloadKey)
  window.location.assign(reloadPath)
  return true
}
